import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import AnimatedSpinner from 'Components/AnimatedSpinner';

import { formatDate } from 'utlis/string_format';

import ProductForm from './ProductForm';
import ReviewCompleted from './ReviewCompleted';

import panelStyles from './styles.module.scss';

const TAB_OPTIONS = {
  REVIEW: 'review',
  CONTEXT: 'context',
};

const ReviewResults = ({
  isReviewComplete,
  reviewData,
  title,
  productDescription,
  audience,
  businessGoal,
  onCategoryClick,
}) => {
  const [selectedTab, setSelectedTab] = useState(TAB_OPTIONS.REVIEW);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef(null);

  const handleScroll = useCallback(() => {
    setIsScrolling(true);
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 1000);
  }, []);

  useEffect(() => () => clearTimeout(scrollTimerRef.current), []);

  return (
    <div
      className={cn(
        'flex-[3] bg-white rounded-2xl shadow-lg flex flex-col mx-8 min-h-0',
        panelStyles.createReviewLeftPanel
      )}
    >
      <div
        className="flex flex-col w-full bg-white rounded-2xl shadow-lg z-10 h-full overflow-hidden"
        style={{
          boxShadow: '0px 4px 16px -2px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)',
        }}
      >
        <div className={cn('flex flex-col items-start w-full flex-1 min-h-0', panelStyles.reviewResultsInner)}>
          <div className="flex w-full justify-center border-b mb-8 flex-shrink-0">
            <p
              className={cn(
                'flex flex-1 align-center justify-center cursor-pointer pb-12 border-b-2',
                {
                  'text-primary-500 border-b-2 border-b-primary-500': selectedTab === TAB_OPTIONS.REVIEW,
                  'border-b-white text-gray-500': selectedTab !== TAB_OPTIONS.REVIEW,
                }
              )}
              onClick={() => setSelectedTab(TAB_OPTIONS.REVIEW)}
            >
              <span>
                Review results
              </span>
            </p>
            <p
              className={cn(
                'flex flex-1 align-center justify-center cursor-pointer pb-12 border-b-2',
                {
                  'text-primary-500 border-b-primary-500': selectedTab === TAB_OPTIONS.CONTEXT,
                  'border-b-white text-gray-500': selectedTab !== TAB_OPTIONS.CONTEXT,
                }
              )}
              onClick={() => setSelectedTab(TAB_OPTIONS.CONTEXT)}
            >
              <span>
                Context
              </span>
            </p>
          </div>

          <div
            className={cn(
              'flex-1 min-h-0 overflow-y-auto w-full pb-24',
              panelStyles['scrollable-content'],
              { [panelStyles['is-scrolling']]: isScrolling }
            )}
            onScroll={handleScroll}
          >
            {selectedTab === TAB_OPTIONS.CONTEXT && (
              <div className="w-full">
                <ProductForm
                  title={title}
                  productDescription={productDescription}
                  audience={audience}
                  businessGoal={businessGoal}
                  reviewData={reviewData}
                  mode="display"
                />
              </div>
            )}
            {selectedTab === TAB_OPTIONS.REVIEW && (
              isReviewComplete
                ? (
                  <ReviewCompleted title={title} reviewData={reviewData} onCategoryClick={onCategoryClick} />
                )
                : (
                  <div className="flex flex-col justify-center items-center gap-10 w-full h-full">
                    <AnimatedSpinner />
                    <div className="flex flex-col items-center gap-4 mt-32">
                      <h2 className="sm:text-xl 2xl:text-2xl font-semibold text-gray-900 text-center">
                        Your review is being processed
                      </h2>
                      <p className="sm:text-md 2xl:text-lg text-gray-900 text-center">
                        This might take a few minutes
                      </p>
                    </div>
                  </div>
                )
            )}
          </div>
        </div>

        <div className={panelStyles['review-footer']}>
          <div className="flex flex-col gap-8 text-xs">
            <span className="text-gray-500 h-4">
              Last updated
            </span>
            <span className="text-gray-900 h-4">
              {reviewData?.updated_at ? formatDate(reviewData?.updated_at) : 'Just now'}
            </span>
          </div>
        </div>
      </div>
    </div>

  );
};

ReviewResults.propTypes = {
  isReviewComplete: PropTypes.bool.isRequired,
  reviewData: PropTypes.object,
  title: PropTypes.string.isRequired,
  productDescription: PropTypes.string.isRequired,
  audience: PropTypes.string,
  businessGoal: PropTypes.string,
  onCategoryClick: PropTypes.func,
};

export default ReviewResults;
